import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import ShubhHeader from './ShubhHeader';
import ShubhBottomNav from './ShubhBottomNav';
import { 
  Heart, Hand, Calendar, Star, Users, BookOpen,
  Sparkles, TrendingUp, Moon, Sun, ChevronRight
} from 'lucide-react';
import '../../styles/shubh-design-system.css';

const ShubhExplorePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isHindi = language === 'hi';

  const categories = [
    {
      title: isHindi ? 'कुंडली और चार्ट' : 'Kundli & Charts',
      items: [
        { 
          icon: Star, 
          label: isHindi ? 'जन्म कुंडली' : 'Janam Kundli',
          desc: isHindi ? 'अपनी जन्म कुंडली देखें' : 'View your birth chart',
          path: '/janam-kundli',
          color: '#F5B942'
        },
        { 
          icon: Heart, 
          label: isHindi ? 'कुंडली मिलान' : 'Kundli Milan',
          desc: isHindi ? 'अनुकूलता जांचें' : 'Check compatibility',
          path: '/kundli-milan',
          color: '#F6C7D3'
        },
      ]
    },
    {
      title: isHindi ? 'भविष्यवाणी' : 'Predictions',
      items: [
        { 
          icon: Sun, 
          label: isHindi ? 'दैनिक राशिफल' : 'Daily Rashifal',
          desc: isHindi ? 'आज का मार्गदर्शन' : "Today's guidance",
          path: '/rashifal',
          color: '#FFB74D'
        },
        { 
          icon: TrendingUp, 
          label: isHindi ? 'वार्षिक पूर्वानुमान' : 'Yearly Forecast',
          desc: isHindi ? `${new Date().getFullYear()} का विस्तृत विश्लेषण` : `Detailed ${new Date().getFullYear()} analysis`,
          path: '/yearly-forecast',
          color: '#81C784'
        },
      ]
    },
    {
      title: isHindi ? 'विशेष सुविधाएं' : 'Special Features',
      items: [
        { 
          icon: Hand, 
          label: isHindi ? 'हस्तरेखा विज्ञान' : 'Palm Reading',
          desc: isHindi ? 'AI-संचालित हस्तरेखा विश्लेषण' : 'AI-powered palm analysis',
          path: '/palm-reading',
          color: '#A5D6A7'
        },
        { 
          icon: Sparkles, 
          label: isHindi ? 'वैदिक गुरु' : 'Vedic Guru',
          desc: isHindi ? 'आवाज से पूछें' : 'Ask with your voice',
          path: '/vedic-guru',
          color: '#FFAB91',
          badge: 'Voice AI'
        },
      ]
    },
    {
      title: isHindi ? 'पंचांग और कैलेंडर' : 'Panchang & Calendar',
      items: [
        { 
          icon: Calendar, 
          label: isHindi ? 'हिंदू पंचांग' : 'Hindu Panchang',
          desc: isHindi ? 'तिथि, नक्षत्र, योग' : 'Tithi, Nakshatra, Yoga',
          path: '/hindu-calendar',
          color: '#90CAF9'
        },
        { 
          icon: Moon, 
          label: isHindi ? 'चंद्र कैलेंडर' : 'Lunar Calendar',
          desc: isHindi ? 'चंद्र चरण और त्योहार' : 'Moon phases & festivals',
          path: '/hindu-calendar',
          color: '#B39DDB'
        },
      ]
    },
    {
      title: isHindi ? 'परिवार' : 'Family',
      items: [
        { 
          icon: Users, 
          label: isHindi ? 'पारिवारिक अनुकूलता' : 'Family Compatibility',
          desc: isHindi ? 'परिवार के सदस्यों की अनुकूलता' : 'Family member compatibility',
          path: '/family-compatibility',
          color: '#FFCC80'
        },
      ]
    },
  ];

  return (
    <div className="shubh-page" style={{ background: 'var(--shubh-ivory)' }}>
      <ShubhHeader title={isHindi ? 'खोजें' : 'Explore'} />

      <div className="shubh-page-content" style={{ paddingTop: 'var(--space-4)' }}>
        {/* Search placeholder */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div 
            className="shubh-input flex items-center gap-3 cursor-pointer"
            onClick={() => {/* TODO: Open search */}}
            style={{ 
              background: 'var(--pure-white)',
              color: 'var(--ink-muted)'
            }}
          >
            <Sparkles className="w-5 h-5" style={{ color: 'var(--ink-muted)' }} />
            <span>{isHindi ? 'खोजें...' : 'Search features...'}</span>
          </div>
        </motion.div>

        {/* Categories */}
        {categories.map((category, catIndex) => (
          <motion.section 
            key={category.title}
            className="shubh-section"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: catIndex * 0.1 }}
          >
            <h3 
              className="text-sm font-semibold uppercase tracking-wide mb-3"
              style={{ color: 'var(--ink-muted)' }}
            >
              {category.title}
            </h3>

            <div className="space-y-2">
              {category.items.map((item, index) => (
                <button
                  key={item.path + index}
                  onClick={() => navigate(item.path)}
                  className="shubh-card shubh-card-interactive w-full text-left p-4"
                  data-testid={`explore-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: item.color }}
                    >
                      <item.icon className="w-6 h-6" style={{ color: 'var(--ink-dark)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium" style={{ color: 'var(--ink-dark)' }}>
                          {item.label}
                        </h4>
                        {item.badge && (
                          <span 
                            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                            style={{
                              background: 'rgba(234, 88, 12, 0.15)',
                              color: 'var(--vermilion)'
                            }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
                        {item.desc}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--ink-muted)' }} />
                  </div>
                </button>
              ))}
            </div>
          </motion.section>
        ))}
      </div>

      <ShubhBottomNav />
    </div>
  );
};

export default ShubhExplorePage;
